import { cn } from "@newxsystems/shared";
import { VelvetCardLink } from "../../../../modules/content/components/VelvetCardLink/VelvetCardLink";
import { VelvetCommonContent } from "../../../../modules/content/components/VelvetCommonContent/VelvetCommonContent";
import { VelvetMedia } from "../../../../modules/content/components/VelvetMedia/VelvetMedia";
import { VelvetRichText } from "../../../../modules/content/components/VelvetRichText/VelvetRichText";
import { VelvetSection } from "../../../../modules/content/components/VelvetSection/VelvetSection";
import type { ITestimonialGridBlockProps } from "./TestimonialGridBlock.props";

const columns = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
} as const;
const spans = {
  1: "lg:col-span-1",
  2: "lg:col-span-2",
  3: "lg:col-span-3",
  4: "lg:col-span-4",
} as const;

export const TestimonialGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: ITestimonialGridBlockProps) => {
  if (!items?.length) return null;

  return (
    <VelvetSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-16 md:gap-24">
        <VelvetCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        <div
          className={cn(
            "grid grid-cols-1 border-t border-parchment/50 sm:grid-cols-2",
            columns[columnCount || 3],
          )}
        >
          {items.map((item, index) => (
            <VelvetCardLink
              className={cn(
                "flex min-h-96 flex-col border-b border-parchment/20 px-0 py-8 text-parchment sm:px-6 sm:first:pl-0 sm:last:pr-0 md:min-h-[28rem] md:py-10",
                spans[item.columnSpan || 1],
              )}
              key={index}
              link={item.cardLink}
            >
              <div className="flex items-center gap-4">
                {item.portrait ? (
                  <VelvetMedia
                    aspectRatio="1:1"
                    className="velvet-arch size-16 shrink-0 border border-parchment/20"
                    media={item.portrait.image}
                    sizes="56px"
                  />
                ) : null}
                <div>
                  <p className="font-velvet-body text-lg italic">{item.name}</p>
                  {item.attribution ? (
                    <p className="mt-1 font-velvet-mono text-xs text-smoke">{item.attribution}</p>
                  ) : null}
                </div>
              </div>
              <blockquote className="mt-auto pt-16">
                <span
                  aria-hidden="true"
                  className="mb-1 block font-velvet-body text-6xl italic leading-none text-thread"
                >
                  “
                </span>
                <VelvetRichText
                  className="font-velvet-display text-3xl font-black uppercase leading-[0.95] tracking-[-0.025em] text-parchment md:text-4xl"
                  content={item.quote}
                />
              </blockquote>
            </VelvetCardLink>
          ))}
        </div>
      </div>
    </VelvetSection>
  );
};

TestimonialGridBlock.displayName = "Block::TestimonialGridBlock";
